import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Search,
  Clock,
  CheckCircle,
  ChefHat,
  Truck,
  AlertCircle,
  CreditCard,
  Trash2,
  XCircle
} from 'lucide-react';
import { formatCurrency, getAllOrders, deleteOrder } from '../../../services/adminApi';

const AdminOrders = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [orders, setOrders] = useState([]);

  const statusOptions = [
    { value: 'all', label: 'Toutes les commandes' },
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmé' },
    { value: 'preparing', label: 'En préparation' },
    { value: 'ready', label: 'Prêt' },
    { value: 'delivered', label: 'Livré' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement commandes:', err);
      setError(err?.message || 'Erreur lors du chargement des commandes');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try {
      await deleteOrder(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      setError(err?.message || 'Erreur lors de la suppression');
    }
  };

  const filteredOrders = orders.filter((order) => {
    const ordLower = `#ord-${order.id}`;
    const idStr = String(order.id);
    const email = (order.email || '').toLowerCase();
    const phone = (order.phone || '').toLowerCase();
    const address = (order.address || '').toLowerCase();
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !term ||
      ordLower.includes(term) ||
      idStr.includes(term) ||
      email.includes(term) ||
      phone.includes(term) ||
      address.includes(term) ||
      (order.customer_id && String(order.customer_id).includes(term));
    const st = (order.status || '').toLowerCase();
    const matchesFilter = filterStatus === 'all' || st === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const da = new Date(a?.created || a?.created_at || 0).getTime();
    const db = new Date(b?.created || b?.created_at || 0).getTime();
    return db - da;
  });

  const formatOrderDate = (order) => {
    const raw = order?.created || order?.created_at;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  const itemsSummary = (order) => {
    const items = order?.items;
    if (!Array.isArray(items) || items.length === 0) return '—';
    const n = items.length;
    const first =
      items[0]?.dish?.name ||
      items[0]?.name ||
      (items[0]?.dish_id != null ? `Plat #${items[0].dish_id}` : 'Article');
    return n === 1 ? first : `${n} articles · ${first}…`;
  };

  const orderTotal = (order) => {
    if (order.paid_amount != null) return Number(order.paid_amount);
    const items = order.items || [];
    return items.reduce((sum, it) => sum + (Number(it.total_price) || 0), 0);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'pending':
        return 'text-amber-700 bg-amber-50 border border-amber-200';
      case 'confirmed':
        return 'text-blue-700 bg-blue-50 border border-blue-200';
      case 'preparing':
        return 'text-sky-700 bg-sky-50 border border-sky-200';
      case 'ready':
        return 'text-emerald-700 bg-emerald-50 border border-emerald-200';
      case 'delivered':
        return 'text-slate-700 bg-slate-100 border border-slate-200';
      case 'cancelled':
        return 'text-rose-700 bg-rose-50 border border-rose-200';
      default:
        return 'text-slate-700 bg-slate-100 border border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'confirmed':
        return <CheckCircle className="w-4 h-4" />;
      case 'preparing':
        return <ChefHat className="w-4 h-4" />;
      case 'ready':
        return <CheckCircle className="w-4 h-4" />;
      case 'delivered':
        return <Truck className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'pending':
        return 'En attente';
      case 'confirmed':
        return 'Confirmé';
      case 'preparing':
        return 'En préparation';
      case 'ready':
        return 'Prêt';
      case 'delivered':
        return 'Livré';
      case 'cancelled':
        return 'Annulé';
      default:
        return status || 'Inconnu';
    }
  };

  const clientLabel = (order) => {
    if (order.customer_id) return `Client #${order.customer_id}`;
    return 'Invité';
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion des commandes</h1>
        <p className="text-gray-600">Suivez et gérez toutes les commandes de la plateforme</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      <div className="bg-white p-4 rounded-xl border border-gray-200 mb-4">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="N° commande, client, e-mail, téléphone, adresse…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-700"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFilterStatus(option.value)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  filterStatus === option.value
                    ? 'bg-primary-700 text-white border-primary-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-900 mb-3">Liste des commandes</h2>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {sortedOrders.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune commande trouvée</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all'
                ? 'Aucune commande ne correspond à vos critères.'
                : 'Aucune commande pour le moment.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600">
                  <th className="px-3 py-3 font-semibold whitespace-nowrap">N°</th>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap">Date</th>
                  <th className="px-3 py-3 font-semibold min-w-[8rem]">Client</th>
                  <th className="px-3 py-3 font-semibold min-w-[9rem] hidden md:table-cell">E-mail</th>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap hidden lg:table-cell">
                    Téléphone
                  </th>
                  <th className="px-3 py-3 font-semibold min-w-[10rem] hidden lg:table-cell">Adresse</th>
                  <th className="px-3 py-3 font-semibold hidden xl:table-cell">Articles</th>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap text-right">Total</th>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap hidden sm:table-cell">
                    Paiement
                  </th>
                  <th className="px-3 py-3 font-semibold whitespace-nowrap">Statut</th>
                  <th className="px-3 py-3 font-semibold text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50/90 align-top">
                    <td className="px-3 py-3 font-medium text-gray-900 whitespace-nowrap">
                      #ORD-{order.id}
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                      {formatOrderDate(order)}
                    </td>
                    <td className="px-3 py-3 text-gray-800">{clientLabel(order)}</td>
                    <td className="px-3 py-3 text-gray-600 hidden md:table-cell max-w-[12rem] truncate">
                      {order.email || '—'}
                    </td>
                    <td className="px-3 py-3 text-gray-600 hidden lg:table-cell whitespace-nowrap">
                      {order.phone || '—'}
                    </td>
                    <td
                      className="px-3 py-3 text-gray-600 hidden lg:table-cell max-w-[14rem] truncate"
                      title={order.address || ''}
                    >
                      {order.address || '—'}
                    </td>
                    <td
                      className="px-3 py-3 text-gray-600 hidden xl:table-cell max-w-[12rem] truncate"
                      title={itemsSummary(order)}
                    >
                      {itemsSummary(order)}
                    </td>
                    <td
                      className="px-3 py-3 text-right font-semibold whitespace-nowrap"
                      style={{ color: 'rgb(5, 70, 37)' }}
                    >
                      {formatCurrency(orderTotal(order))}
                    </td>
                    <td className="px-3 py-3 hidden sm:table-cell whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-gray-700">
                        <CreditCard className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                        {order.paid ? 'Payé' : 'Non payé'}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {getStatusIcon(order.status)}
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDeleteOrder(order.id)}
                        className="inline-flex items-center justify-center p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100"
                        title="Supprimer la commande"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sortedOrders.length > 0 && (
        <p className="mt-4 text-sm text-gray-600">
          {sortedOrders.length} commande{sortedOrders.length > 1 ? 's' : ''} affichée
          {sortedOrders.length > 1 ? 's' : ''}
          {orders.length !== sortedOrders.length ? ` (${orders.length} au total)` : ''}
        </p>
      )}
    </div>
  );
};

export default AdminOrders;
